import { RADII, SPACING, STROKES } from "../../../lib/brand";
import { PANE_THEMES } from "../../../components/brand/paneThemes";
import type { Href } from "expo-router";
import { router, useFocusEffect } from "expo-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ScrollView, Text, View } from "react-native";

import { listAllStudents, type AdminStudentListRow } from "../../../lib/admin";
import { exportRowsAsCsv, todayDateForFilename } from "../../../lib/csvExport";
import { useAdminListFilter } from "../../../lib/useAdminListFilter";
import {
  AdminAvatar,
  AdminButton,
  AdminEmpty,
  AdminInput,
  AdminLoading,
  AdminPageHeader,
  FilterChip,
  ListRow,
  ListRowGroup,
} from "../../../components/admin/ui";

const THEME = PANE_THEMES.Admin;
const UNI_CAP = 12;

export default function AdminStudentsScreen() {
  const [rows, setRows] = useState<AdminStudentListRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAllUnis, setShowAllUnis] = useState(false);
  const hasLoadedRef = useRef(false);

  const filter = useAdminListFilter({
    keys: ["q", "uni", "grad"],
    defaults: { q: "", uni: "all", grad: "all" },
  });
  const search = filter.get("q");
  const uniFilter = filter.get("uni");
  const gradFilter = filter.get("grad");

  const [searchInput, setSearchInput] = useState(search);
  useEffect(() => setSearchInput(search), [search]);
  useEffect(() => {
    const t = setTimeout(() => {
      if (searchInput !== search) filter.set({ q: searchInput });
    }, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const load = useCallback(async () => {
    if (!hasLoadedRef.current) setLoading(true);
    try {
      setRows(await listAllStudents());
    } finally {
      hasLoadedRef.current = true;
      setLoading(false);
    }
  }, []);

  useFocusEffect(
    useCallback(() => {
      void load();
    }, [load])
  );

  const universities = useMemo(() => {
    const set = new Set<string>();
    for (const r of rows) if (r.university?.trim()) set.add(r.university.trim());
    return [...set].sort();
  }, [rows]);

  const gradYears = useMemo(() => {
    const set = new Set<number>();
    for (const r of rows) if (r.graduation_year) set.add(r.graduation_year);
    return [...set].sort();
  }, [rows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (uniFilter !== "all" && r.university !== uniFilter) return false;
      if (gradFilter !== "all" && String(r.graduation_year ?? "") !== gradFilter) return false;
      if (q) {
        const hay = `${r.full_name ?? ""} ${r.email ?? ""} ${r.university ?? ""} ${r.major ?? ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [rows, search, uniFilter, gradFilter]);

  const visibleUnis = showAllUnis ? universities : universities.slice(0, UNI_CAP);

  return (
    <ScrollView style={{ flex: 1, backgroundColor: THEME.pageBg }} contentContainerStyle={{ paddingBottom: 32 }}>
      <View style={{ padding: SPACING.lg, maxWidth: 1080 }}>
        <AdminPageHeader
          theme={THEME}
          eyebrow="People · Students"
          title="All students"
          subtitle="Global student directory."
          actions={
            <>
              <AdminButton theme={THEME} label="Verification review →" variant="outline" size="sm" onPress={() => router.push("/admin/students/review" as Href)} />
              <AdminButton
                theme={THEME}
                label="Export CSV"
                variant="ghost"
                size="sm"
                onPress={() =>
                  exportRowsAsCsv({
                    filename: `students_${todayDateForFilename()}.csv`,
                    auditPage: "students",
                    columns: [
                      { header: "Name", get: (r: AdminStudentListRow) => r.full_name ?? "" },
                      { header: "Email", get: (r: AdminStudentListRow) => r.email ?? "" },
                      { header: "University", get: (r: AdminStudentListRow) => r.university ?? "" },
                      { header: "Major", get: (r: AdminStudentListRow) => r.major ?? "" },
                      { header: "Graduation year", get: (r: AdminStudentListRow) => r.graduation_year ?? "" },
                      { header: "Completed competitions", get: (r: AdminStudentListRow) => r.completed_competitions },
                    ],
                    rows: filtered,
                  })
                }
              />
            </>
          }
        />

        <View style={{ padding: 12, borderRadius: RADII.md, backgroundColor: THEME.surfaceRaisedBg, borderWidth: STROKES.hairline, borderColor: THEME.border, gap: 10 }}>
          <AdminInput theme={THEME} value={searchInput} onChangeText={setSearchInput} placeholder="Search name, email, university, major" />
          {universities.length > 0 ? (
            <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
              <Text style={{ fontFamily: THEME.bodyMediumFont, fontSize: 11, color: THEME.textOnSurfaceDim }}>University:</Text>
              <FilterChip theme={THEME} label="All" active={uniFilter === "all"} onPress={() => filter.set({ uni: "all" })} />
              {visibleUnis.map((u) => (
                <FilterChip key={u} theme={THEME} label={u} active={uniFilter === u} onPress={() => filter.set({ uni: u })} />
              ))}
              {universities.length > UNI_CAP ? (
                <FilterChip theme={THEME} label={showAllUnis ? "Show fewer" : `+${universities.length - UNI_CAP} more`} active={false} onPress={() => setShowAllUnis((s) => !s)} />
              ) : null}
            </View>
          ) : null}
          {gradYears.length > 0 ? (
            <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
              <Text style={{ fontFamily: THEME.bodyMediumFont, fontSize: 11, color: THEME.textOnSurfaceDim }}>Grad year:</Text>
              <FilterChip theme={THEME} label="All" active={gradFilter === "all"} onPress={() => filter.set({ grad: "all" })} />
              {gradYears.map((y) => (
                <FilterChip key={y} theme={THEME} label={String(y)} active={gradFilter === String(y)} onPress={() => filter.set({ grad: String(y) })} />
              ))}
            </View>
          ) : null}
        </View>

        <View style={{ marginTop: SPACING.md }}>
          {loading ? (
            <AdminLoading theme={THEME} label="Loading students…" />
          ) : filtered.length === 0 ? (
            <AdminEmpty
              theme={THEME}
              title="No students match."
              body={search.trim() ? "Try a different search." : "No students in the directory yet."}
            />
          ) : (
            <ListRowGroup theme={THEME}>
              {filtered.map((row, i) => (
                <ListRow
                  key={row.id}
                  theme={THEME}
                  first={i === 0}
                  onPress={() => router.push(`/admin/student/${row.id}` as Href)}
                  leading={<AdminAvatar theme={THEME} name={row.full_name} fallback="S" />}
                  title={row.full_name ?? "Student"}
                  subtitle={
                    <View>
                      <Text style={{ fontFamily: THEME.bodyFont, fontSize: 11.5, color: THEME.textOnSurfaceDim }} numberOfLines={1}>
                        {row.email ?? "—"}
                      </Text>
                      <Text style={{ marginTop: 1, fontFamily: THEME.bodyFont, fontSize: 11, color: THEME.textOnSurfaceFaint }} numberOfLines={1}>
                        {row.university ?? "—"} · {row.major ?? "—"} · {row.graduation_year ?? "—"}
                      </Text>
                    </View>
                  }
                  meta={`${row.completed_competitions} completed`}
                />
              ))}
            </ListRowGroup>
          )}
        </View>
      </View>
    </ScrollView>
  );
}
